import React from "react";
import { Outlet, useNavigate, Link } from "react-router-dom";
import { BottomNav } from "../../components/BottomNav";
import { useAuth } from "../../context/AuthContext";
import { deptLabel, storeAbbr } from "../../lib/labels";
import { LogOut, ArrowLeft } from "lucide-react";
import { Toaster } from "sonner";
import PushPermissionPrompt from "../../components/PushPermissionPrompt";
import OutboxIndicator from "../../components/OutboxIndicator";

export default function EmployeeLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isManager = user?.role === "manager";
  const abbr = storeAbbr(user);
  const abbrIsUnknown = abbr === "STORE UNKNOWN";
  const onLogout = async () => {
    const res = await logout();
    if (!res.ok && res.pending) {
      const ok = window.confirm(
        `You have ${res.pending} unsynced action${res.pending === 1 ? "" : "s"} on this device. ` +
        `Logging out will discard them and they will NOT be sent to the server. Continue?`,
      );
      if (!ok) return;
      await logout({ force: true });
    }
    nav("/login", { replace: true });
  };
  return (
    <div className="min-h-screen bg-paper pb-24" data-testid="employee-layout">
      <header className="sticky top-0 z-20 bg-white border-b border-border">
        <div className="px-4 h-14 flex items-center justify-between">
          <div className="flex items-baseline gap-2 min-w-0">
            <div className="font-display font-black text-lg leading-none">
              Shelf<span className="text-brand">Life</span>
            </div>
            {isManager ? (
              <>
                <span className="data-sku truncate" data-testid="employee-header-scope-manager">
                  · Manager
                </span>
                <span
                  className={`data-sku ${abbrIsUnknown ? "text-shelf-expired" : ""}`}
                  data-testid="employee-header-store-abbr"
                  title={user?.store_name || ""}
                  aria-label={user?.store_name ? `Store ${user.store_name}` : "Store unknown"}
                >
                  · {abbr}
                </span>
              </>
            ) : (
              <>
                <span className="data-sku truncate">· {deptLabel(user?.department_name) || "—"}</span>
                <span
                  className={`data-sku ${abbrIsUnknown ? "text-shelf-expired" : ""}`}
                  data-testid="employee-header-store-abbr"
                  title={user?.store_name || ""}
                  aria-label={user?.store_name ? `Store ${user.store_name}` : "Store unknown"}
                >
                  · {abbr}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {isManager && (
              <Link
                to="/admin"
                className="h-9 px-2 flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider text-ink/70 hover:text-ink"
                data-testid="manager-back-to-console"
                aria-label="Back to Manager Console"
              >
                <ArrowLeft size={14} /> Console
              </Link>
            )}
            <span className="font-mono text-xs text-muted-foreground hidden sm:inline truncate max-w-[180px]">{user?.email}</span>
            <button
              onClick={onLogout}
              className="h-9 px-2 -mr-2 flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider text-ink/70 hover:text-ink"
              data-testid="employee-logout"
              aria-label="Sign out"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto">
        <PushPermissionPrompt />
        <Outlet />
      </main>
      <BottomNav />
      <OutboxIndicator />
      <Toaster position="top-center" richColors />
    </div>
  );
}
